import { Avatar, Box, Button, Link, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Group, UserInfo } from "../../../../types";
import { useDataFormatter } from "../../../../hooks/useDataFormatter";
import { useProfileData } from "../../../../hooks/useProfileData";
import { colors } from "../../../../theme/palette";
import { VerificationIcon } from "../../../../assets/icons/MUI/NetworkIcons";
import { InviteIcon, RemoveIcon } from "../../../../assets/icons/MUI/EventsIcons";
import { useCreateEventContext } from "../cxt";

const cardStyle = {
  display: "flex",
  flexDirection: "column",
  rowGap: "14px",
  padding: "12px",
  position: "relative",
  borderRadius: "4px",
  background: colors.White,
  boxShadow: "0px 12px 20px 0px rgba(0, 0, 0, 0.04), 0px 0px 2px 0px rgba(0, 0, 0, 0.06)",
  height: "250px",
  maxWidth: "100%",
  minWidth: "0",
};

const linkStyle = {
  textDecoration: "none",
  color: colors.dark1000,
  textAlign: "center",
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: "normal",
  maxWidth: "80%",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  cursor: "pointer",
};

type UserCardProps = {
  user: UserInfo;
  maxInvitations?: number;
  currentSelectedCount?: number;
  onInviteLimitReached?: () => void;
};

const UserCard = ({ user, maxInvitations, currentSelectedCount = 0, onInviteLimitReached }: UserCardProps) => {
  const navigate = useNavigate();

  const {
    fullName,
    firstTwoChr,
    countryMedia,
    isVerified,
    media,
    role,
    profileLink,
    mutualFollowersCount,
    playerPositionShort,
    friendsCount,
    id,
    entityId,
  } = useDataFormatter(user);

  const { state, setState } = useCreateEventContext();

  const selectedEntities = state.selectedEntities;
  const isInvited = selectedEntities.some((entity) => entity.id === id);

  const { data: ownProfileData } = useProfileData();
  const { entityId: entityIdFromCache } = ownProfileData;

  const isOwnProfile = entityId == entityIdFromCache && entityIdFromCache != null;

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  return (
    <Box sx={cardStyle}>
      {isVerified && (
        <VerificationIcon
          sx={{
            position: "absolute",
            top: "72px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: "10",
          }}
        />
      )}
      <Avatar
        sx={{
          width: "72px",
          height: "72px",
          margin: "0 auto 9px",
        }}
        src={media}
      >
        {firstTwoChr}
      </Avatar>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: "3px",
            width: "100%",
            justifyContent: "center",
            marginBottom: "5.5px",
          }}
        >
          <Link sx={linkStyle} onClick={() => navigate(profileLink)}>
            {fullName}
          </Link>
          <img src={countryMedia} height={"9px"} width={"12px"} />
        </Box>
        <Typography
          component="span"
          variant="body12Regular"
          sx={{
            color: colors.Dark1000,
            textAlign: "center",
          }}
        >
          {role}
          {playerPositionShort ? ` . (${playerPositionShort})` : ""}
        </Typography>

        <Typography
          component="span"
          variant="body12Regular"
          sx={{
            color: colors.Dark600,
            textAlign: "center",
          }}
        >
          {friendsCount ? `${friendsCount} ${t("pages.events.createEvent.friends")}` : ""}
        </Typography>

        {isOwnProfile ? (
          <></>
        ) : (
          <Typography
            component="span"
            variant="body12Regular"
            sx={{
              color: colors.Dark600,
              textAlign: "center",
            }}
          >
            {`${mutualFollowersCount} ${t("pages.events.createEvent.commonFollowers")}`}
          </Typography>
        )}
      </Box>

      {isOwnProfile ? (
        <></>
      ) : isInvited ? (
        <Button
          variant="outlined-sm"
          fullWidth
          startIcon={currentLanguage === "en" && <RemoveIcon />}
          endIcon={currentLanguage === "ar" && <RemoveIcon />}
          onClick={() =>
            setState((prevState) => ({
              ...prevState,
              selectedEntities: prevState.selectedEntities.filter((entity: any) => entity.id !== id),
            }))
          }
          sx={{
            marginTop: "auto",
            color: `${colors.Red200} !important`,
            borderColor: `${colors.Red200} !important`,
            background: "transparent !important",
          }}
        >
          {t("pages.events.createEvent.remove")}
        </Button>
      ) : (
        <Button
          variant="secondary-sm"
          fullWidth
          startIcon={currentLanguage === "en" && <InviteIcon />}
          endIcon={currentLanguage === "ar" && <InviteIcon />}
          sx={{
            marginTop: "auto",
          }}
          onClick={() => {
            // Check if adding this user would exceed the limit (only if limit exists)
            if (maxInvitations !== undefined && currentSelectedCount >= maxInvitations) {
              onInviteLimitReached?.();
              return;
            }
            setState((prevState) => ({
              ...prevState,
              selectedEntities: [...prevState.selectedEntities, user],
            }));
          }}
        >
          {t("pages.events.createEvent.invite")}
        </Button>
      )}
    </Box>
  );
};

export default UserCard;
